import { useState, useMemo, useEffect } from 'react';
import { publicApi } from '../api/client.js';
import { UtensilsIcon, BagIcon, ScooterIcon, CartIcon, WhatsAppIcon } from './Icons.jsx';

const getText = (val, locale, fallback = '') => {
  if (!val) return fallback;
  if (typeof val === 'string') return val;
  return val[locale] || val.en || val.ar || Object.values(val)[0] || fallback;
};

/**
 * Cart drawer - slide-up bottom sheet on mobile, sidebar on desktop
 * Props:
 *   isOpen, onClose
 *   items: [{ itemId, name, price, quantity, ... }]
 *   menu: full menu object
 *   locale
 *   onUpdateQty: (itemId, newQty) => void
 *   onRemove: (itemId) => void
 *   onClearCart: () => void
 */
export default function CartDrawer({
  isOpen, onClose, items = [], menu, locale = 'en',
  onUpdateQty, onRemove, onClearCart,
}) {
  const [step, setStep] = useState('cart'); // cart | type | success
  const [orderType, setOrderType] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  const [tableNumber, setTableNumber] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');

  const primaryColor = menu.primary_color || '#7C3AED';
  const currency = menu.currency || '$';

  // Available order types based on menu settings
  const availableTypes = useMemo(() => {
    const types = [];
    if (menu.dine_in_enabled) types.push({ id: 'DINE_IN', label: 'Dine in', Icon: UtensilsIcon, desc: 'Eat at the restaurant' });
    if (menu.takeaway_enabled) types.push({ id: 'TAKEAWAY', label: 'Takeaway', Icon: BagIcon, desc: 'Pick up your order' });
    if (menu.delivery_enabled) types.push({ id: 'DELIVERY', label: 'Delivery', Icon: ScooterIcon, desc: 'Delivered to you' });
    if (menu.whatsapp_ordering_enabled && menu.contact_info?.whatsapp) {
      types.push({ id: 'WHATSAPP', label: 'WhatsApp', Icon: WhatsAppIcon, desc: 'Send your order to the restaurant' });
    }
    return types;
  }, [menu.dine_in_enabled, menu.takeaway_enabled, menu.delivery_enabled, menu.whatsapp_ordering_enabled, menu.contact_info]);

  // Auto-select if only one type available
  useEffect(() => {
    if (availableTypes.length === 1 && !orderType) {
      setOrderType(availableTypes[0].id);
    }
  }, [availableTypes, orderType]);

  const subtotal = useMemo(
    () => items.reduce((s, it) => s + (it.price * it.quantity), 0),
    [items]
  );
  const taxRate = parseFloat(menu.tax_rate || 0);
  const tax = subtotal * (taxRate / 100);
  const deliveryFee = orderType === 'DELIVERY' ? parseFloat(menu.delivery_fee || 0) : 0;
  const total = subtotal + tax + deliveryFee;

  const orderingAvailable = availableTypes.length > 0;

  const handleContinue = () => {
    setErrorMsg('');
    // Always show the type step now (phone needed for TAKEAWAY/DELIVERY, table for DINE_IN)
    if (availableTypes.length === 1) {
      setOrderType(availableTypes[0].id);
    }
    setStep('type');
  };

  const handlePlaceOrder = async (typeOverride) => {
    const type = typeOverride || orderType;
    setErrorMsg('');

    // Validate required fields
    if (type === 'DINE_IN' && !tableNumber.trim()) {
      setErrorMsg('Please enter your table number');
      return;
    }
    if (type === 'DELIVERY' && !deliveryAddress.trim()) {
      setErrorMsg('Please enter your delivery address');
      return;
    }
    // Phone required for TAKEAWAY and DELIVERY (and WHATSAPP, but optional UI handles that)
    if ((type === 'TAKEAWAY' || type === 'DELIVERY') && !phoneNumber.trim()) {
      setErrorMsg('Please enter your phone number');
      return;
    }

    // WhatsApp orders are submitted with via='whatsapp' so the server returns a wa.me URL
    return submitOrder(type === 'WHATSAPP' ? 'whatsapp' : 'online', type);
  };

  // Shared submit logic — `via` is 'online' or 'whatsapp'
  const submitOrder = async (via, type) => {
    setSubmitting(true);
    try {
      const payload = {
        type,
        tableNumber: type === 'DINE_IN' ? tableNumber.trim() : null,
        deliveryAddress: type === 'DELIVERY' ? deliveryAddress.trim() : null,
        customerPhone: phoneNumber.trim() || null,
        items: items.map(it => ({
          itemId: it.itemId,
          name: it.name,
          price: it.price,
          quantity: it.quantity,
        })),
        via,
      };

      const result = await publicApi.placeOrder(menu.slug, payload);

      // For WhatsApp: open the wa.me URL the server returned, then show success
      if (via === 'whatsapp' && result.whatsappUrl) {
        window.open(result.whatsappUrl, '_blank', 'noopener');
      }

      setOrderResult(result);
      setStep('success');
      onClearCart?.();
    } catch (err) {
      setErrorMsg(err.response?.data?.error || 'Failed to place order');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setStep('cart');
    setOrderType(availableTypes.length === 1 ? availableTypes[0].id : null);
    setTableNumber('');
    setDeliveryAddress('');
    setPhoneNumber('');
    setOrderResult(null);
    setErrorMsg('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      onClick={handleClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(15,23,42,0.6)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'white',
          width: '100%', maxWidth: '480px',
          maxHeight: '92vh', overflowY: 'auto',
          borderRadius: '24px 24px 0 0',
          fontFamily: 'Rubik, Inter, sans-serif',
          animation: 'slideUp 0.25s ease-out',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #E4E4E7',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, background: 'white', zIndex: 5,
        }}>
          <div>
            <div style={{ fontSize: '17px', fontWeight: 700, letterSpacing: '-0.3px' }}>
              {step === 'cart' && 'Your order'}
              {step === 'type' && 'How would you like it?'}
              {step === 'success' && 'Order placed!'}
            </div>
            {step === 'cart' && items.length > 0 && (
              <div style={{ fontSize: '12px', color: '#64748B' }}>
                {items.length} item{items.length !== 1 ? 's' : ''}
              </div>
            )}
          </div>
          <button
            onClick={handleClose}
            style={{
              width: '32px', height: '32px',
              border: 'none', background: '#F1F5F9',
              borderRadius: '50%', fontSize: '18px',
              cursor: 'pointer', color: '#475569',
            }}
          >×</button>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 20px' }}>
          {step === 'cart' && (
            <CartView
              items={items}
              currency={currency}
              primaryColor={primaryColor}
              locale={locale}
              subtotal={subtotal}
              tax={tax}
              taxRate={taxRate}
              deliveryFee={deliveryFee}
              total={total}
              orderingAvailable={orderingAvailable}
              onUpdateQty={onUpdateQty}
              onRemove={onRemove}
              onContinue={handleContinue}
            />
          )}

          {step === 'type' && (
            <TypeView
              menu={menu}
              types={availableTypes}
              orderType={orderType}
              setOrderType={setOrderType}
              tableNumber={tableNumber}
              setTableNumber={setTableNumber}
              deliveryAddress={deliveryAddress}
              setDeliveryAddress={setDeliveryAddress}
              phoneNumber={phoneNumber}
              setPhoneNumber={setPhoneNumber}
              currency={currency}
              primaryColor={primaryColor}
              subtotal={subtotal}
              tax={tax}
              taxRate={taxRate}
              deliveryFee={deliveryFee}
              total={total}
              submitting={submitting}
              errorMsg={errorMsg}
              onBack={() => setStep('cart')}
              onPlaceOrder={() => handlePlaceOrder()}
            />
          )}

          {step === 'success' && orderResult && (
            <SuccessView
              orderResult={orderResult}
              currency={currency}
              primaryColor={primaryColor}
              onClose={handleClose}
            />
          )}
        </div>

        <style>{`
          @keyframes slideUp {
            from { transform: translateY(100%); }
            to { transform: translateY(0); }
          }
        `}</style>
      </div>
    </div>
  );
}

function CartView({ items, currency, primaryColor, locale, subtotal, tax, taxRate, deliveryFee, total,
  orderingAvailable, onUpdateQty, onRemove, onContinue }) {
  if (items.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 20px' }}>
        <div style={{
          width: '64px', height: '64px',
          margin: '0 auto 14px',
          borderRadius: '50%',
          background: '#F1F5F9',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#94A3B8',
        }}>
          <CartIcon size={28} />
        </div>
        <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '4px' }}>Your cart is empty</div>
        <div style={{ fontSize: '12px', color: '#64748B' }}>
          Browse the menu and add items to your order
        </div>
      </div>
    );
  }

  return (
    <>
      {items.map(item => {
        const name = getText(item.name, locale, 'Item');
        return (
          <div
            key={item.itemId}
            style={{
              display: 'flex', alignItems: 'center', gap: '12px',
              padding: '12px 0',
              borderBottom: '1px solid #F1F5F9',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '2px' }}>{name}</div>
              <div style={{ fontSize: '12px', color: primaryColor, fontWeight: 700 }}>
                {currency} {item.price}
              </div>
            </div>

            {/* Quantity controls */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              background: '#FAFAFB', borderRadius: '999px', padding: '4px',
            }}>
              <button
                onClick={() => onUpdateQty(item.itemId, Math.max(0, item.quantity - 1))}
                style={{
                  width: '28px', height: '28px',
                  border: 'none', background: primaryColor, color: 'white',
                  borderRadius: '50%', cursor: 'pointer',
                  fontSize: '16px', fontWeight: 600,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >−</button>
              <span style={{ minWidth: '20px', textAlign: 'center', fontWeight: 700, fontSize: '13px' }}>
                {item.quantity}
              </span>
              <button
                onClick={() => onUpdateQty(item.itemId, item.quantity + 1)}
                style={{
                  width: '28px', height: '28px',
                  border: 'none', background: primaryColor, color: 'white',
                  borderRadius: '50%', cursor: 'pointer',
                  fontSize: '16px', fontWeight: 600,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >+</button>
            </div>

            <button
              onClick={() => onRemove(item.itemId)}
              style={{
                width: '28px', height: '28px',
                background: 'transparent', border: 'none',
                color: '#94A3B8', cursor: 'pointer',
                fontSize: '14px',
              }}
              aria-label="Remove"
            >×</button>
          </div>
        );
      })}

      {/* Summary */}
      <div style={{
        background: '#FAFAFB', borderRadius: '12px',
        padding: '14px', marginTop: '14px', marginBottom: '14px',
      }}>
        <SummaryRow label="Subtotal" value={`${currency} ${subtotal.toFixed(2)}`} />
        {taxRate > 0 && <SummaryRow label={`Tax (${taxRate}%)`} value={`${currency} ${tax.toFixed(2)}`} />}
        {deliveryFee > 0 && <SummaryRow label="Delivery" value={`${currency} ${deliveryFee.toFixed(2)}`} />}
        <div style={{ height: '1px', background: '#E4E4E7', margin: '8px 0' }} />
        <SummaryRow label="Total" value={`${currency} ${total.toFixed(2)}`} bold primaryColor={primaryColor} />
      </div>

      {/* Continue button */}
      <button
        onClick={onContinue}
        disabled={!orderingAvailable}
        style={{
          width: '100%',
          padding: '14px',
          background: !orderingAvailable ? '#E4E4E7' : primaryColor,
          color: !orderingAvailable ? '#94A3B8' : 'white',
          border: 'none', borderRadius: '12px',
          fontSize: '14px', fontWeight: 700,
          cursor: !orderingAvailable ? 'not-allowed' : 'pointer',
          fontFamily: 'inherit',
          boxShadow: !orderingAvailable ? 'none' : `0 4px 14px ${primaryColor}40`,
        }}
      >
        {!orderingAvailable ? 'Online ordering not available' : 'Continue →'}
      </button>
    </>
  );
}


function TypeView({ menu, types, orderType, setOrderType, tableNumber, setTableNumber,
  deliveryAddress, setDeliveryAddress, phoneNumber, setPhoneNumber,
  currency, primaryColor,
  subtotal, tax, taxRate, deliveryFee, total,
  submitting, errorMsg, onBack, onPlaceOrder }) {

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: '13px', color: '#475569', fontWeight: 600,
          padding: '4px 0', marginBottom: '14px',
          display: 'flex', alignItems: 'center', gap: '4px',
        }}
      >← Back to cart</button>

      {/* Order type cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: types.length === 1 ? '1fr' : `repeat(${Math.min(types.length, 3)}, 1fr)`,
        gap: '8px',
        marginBottom: '16px',
      }}>
        {types.map(t => {
          const isActive = orderType === t.id;
          const Icon = t.Icon;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setOrderType(t.id)}
              style={{
                padding: '14px 10px',
                border: isActive ? `2px solid ${primaryColor}` : '1px solid #E4E4E7',
                background: isActive ? `${primaryColor}10` : 'white',
                borderRadius: '12px',
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: '12px',
                fontWeight: 600,
                color: isActive ? primaryColor : '#475569',
                textAlign: 'center',
                transition: 'all 0.15s',
              }}
            >
              <div style={{
                marginBottom: '8px', display: 'flex', justifyContent: 'center',
                color: isActive ? primaryColor : '#475569',
              }}>
                <Icon size={26} />
              </div>
              <div style={{ marginBottom: '2px' }}>{t.label}</div>
              <div style={{ fontSize: '10px', color: '#94A3B8', fontWeight: 500 }}>{t.desc}</div>
            </button>
          );
        })}
      </div>

      {/* Type-specific fields */}
      {orderType === 'DINE_IN' && (
        <div style={{ marginBottom: '14px' }}>
          <label style={{ fontSize: '12px', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '6px' }}>
            Table number *
          </label>
          <input
            type="text"
            value={tableNumber}
            onChange={e => setTableNumber(e.target.value)}
            placeholder="e.g. 5"
            autoFocus
            style={{
              width: '100%',
              padding: '12px 14px',
              border: '1px solid #E4E4E7',
              borderRadius: '10px',
              fontSize: '14px',
              fontFamily: 'inherit',
              outline: 'none',
            }}
          />
        </div>
      )}

      {orderType === 'DELIVERY' && (
        <div style={{ marginBottom: '14px' }}>
          <label style={{ fontSize: '12px', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '6px' }}>
            Delivery address *
          </label>
          <textarea
            value={deliveryAddress}
            onChange={e => setDeliveryAddress(e.target.value)}
            placeholder="Street, building, floor, apartment..."
            rows={3}
            autoFocus
            style={{
              width: '100%',
              padding: '12px 14px',
              border: '1px solid #E4E4E7',
              borderRadius: '10px',
              fontSize: '14px',
              fontFamily: 'inherit',
              outline: 'none',
              resize: 'vertical',
            }}
          />
        </div>
      )}

      {orderType === 'TAKEAWAY' && (
        <div style={{
          padding: '12px',
          background: 'var(--primary-50)',
          borderRadius: '10px',
          fontSize: '12px',
          color: 'var(--text-2)',
          marginBottom: '14px',
          textAlign: 'center',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
        }}>
          <BagIcon size={14} />
          We'll call you when your order is ready for pickup
        </div>
      )}

      {orderType === 'WHATSAPP' && (
        <div style={{
          padding: '12px',
          background: '#F0FDF4',
          border: '1px solid #BBF7D0',
          borderRadius: '10px',
          fontSize: '12px',
          color: '#166534',
          marginBottom: '14px',
          textAlign: 'center',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
        }}>
          <WhatsAppIcon size={14} />
          Your order will open in WhatsApp — finalize details with the restaurant
        </div>
      )}

      {/* Phone field for TAKEAWAY, DELIVERY, and optional WHATSAPP */}
      {(orderType === 'TAKEAWAY' || orderType === 'DELIVERY' || orderType === 'WHATSAPP') && (
        <div style={{ marginBottom: '14px' }}>
          <label style={{ fontSize: '12px', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '6px' }}>
            Phone number *
          </label>
          <input
            type="tel"
            inputMode="tel"
            value={phoneNumber}
            onChange={e => setPhoneNumber(e.target.value)}
            placeholder="+966 50 123 4567"
            style={{
              width: '100%',
              padding: '12px 14px',
              border: '1px solid #E4E4E7',
              borderRadius: '10px',
              fontSize: '14px',
              fontFamily: 'inherit',
              outline: 'none',
            }}
          />
        </div>
      )}

      {/* Summary */}
      {orderType && (
        <div style={{
          background: '#FAFAFB', borderRadius: '12px',
          padding: '14px', marginBottom: '14px',
        }}>
          <SummaryRow label="Subtotal" value={`${currency} ${subtotal.toFixed(2)}`} />
          {taxRate > 0 && <SummaryRow label={`Tax (${taxRate}%)`} value={`${currency} ${tax.toFixed(2)}`} />}
          {deliveryFee > 0 && <SummaryRow label="Delivery fee" value={`${currency} ${deliveryFee.toFixed(2)}`} />}
          <div style={{ height: '1px', background: '#E4E4E7', margin: '8px 0' }} />
          <SummaryRow label="Total" value={`${currency} ${total.toFixed(2)}`} bold primaryColor={primaryColor} />
        </div>
      )}

      {errorMsg && (
        <div style={{
          padding: '10px 14px', background: '#FEE2E2',
          borderRadius: '8px', fontSize: '12px', color: '#991B1B',
          marginBottom: '14px',
        }}>{errorMsg}</div>
      )}

      <button
        onClick={onPlaceOrder}
        disabled={!orderType || submitting}
        style={{
          width: '100%', padding: '14px',
          background: !orderType || submitting
            ? '#E4E4E7'
            : (orderType === 'WHATSAPP' ? '#25D366' : primaryColor),
          color: !orderType || submitting ? '#94A3B8' : 'white',
          border: 'none', borderRadius: '12px',
          fontSize: '14px', fontWeight: 700,
          cursor: !orderType || submitting ? 'not-allowed' : 'pointer',
          fontFamily: 'inherit',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          boxShadow: !orderType || submitting
            ? 'none'
            : (orderType === 'WHATSAPP' ? '0 4px 14px rgba(37, 211, 102, 0.4)' : `0 4px 14px ${primaryColor}40`),
        }}
      >
        {orderType === 'WHATSAPP' && !submitting && <WhatsAppIcon size={17} />}
        {!orderType
          ? 'Select an order type'
          : submitting
            ? (orderType === 'WHATSAPP' ? 'Opening WhatsApp...' : 'Placing order...')
            : (orderType === 'WHATSAPP'
                ? `Send via WhatsApp · ${currency} ${total.toFixed(2)}`
                : `Place order · ${currency} ${total.toFixed(2)}`)}
      </button>
    </div>
  );
}

function SuccessView({ orderResult, currency, primaryColor, onClose }) {
  return (
    <div style={{ textAlign: 'center', padding: '20px 10px' }}>
      <div style={{
        width: '80px', height: '80px', margin: '0 auto 20px',
        background: '#DCFCE7', borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
      <h3 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '8px', letterSpacing: '-0.3px' }}>
        Order placed successfully!
      </h3>
      <p style={{ fontSize: '13px', color: '#64748B', marginBottom: '24px' }}>
        The restaurant has been notified. They'll prepare your order shortly.
      </p>

      <div style={{
        background: '#FAFAFB', borderRadius: '12px',
        padding: '16px', marginBottom: '20px',
      }}>
        <div style={{ fontSize: '11px', color: '#64748B', marginBottom: '4px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Order number
        </div>
        <div style={{ fontSize: '20px', fontWeight: 700, fontFamily: 'monospace', color: primaryColor, marginBottom: '8px' }}>
          {orderResult.orderNumber}
        </div>
        <div style={{ fontSize: '12px', color: '#475569' }}>
          Total: <b>{currency} {parseFloat(orderResult.total).toFixed(2)}</b>
        </div>
      </div>

      <button
        onClick={onClose}
        style={{
          width: '100%', padding: '12px',
          background: primaryColor, color: 'white',
          border: 'none', borderRadius: '12px',
          fontSize: '13px', fontWeight: 700,
          cursor: 'pointer', fontFamily: 'inherit',
        }}
      >Continue browsing</button>
    </div>
  );
}

function SummaryRow({ label, value, bold, primaryColor }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between',
      padding: '4px 0',
      fontSize: bold ? '15px' : '13px',
      fontWeight: bold ? 700 : 500,
    }}>
      <span style={{ color: bold ? '#0F172A' : '#475569' }}>{label}</span>
      <span style={{ color: bold ? primaryColor : '#0F172A' }}>{value}</span>
    </div>
  );
}
